import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { acksApi, peopleApi } from "../api/client.js";
import { useAuth } from "../auth/AuthContext.jsx";
import { useToast } from "./Toast.jsx";
import { clockTime, dayName, isWeekend, ymd } from "../lib/domain.js";

/* The daily check-back bar: one chip per person expected to work the board
   today, green only when everyone has acknowledged.
 *
 * The artifact build could not tell who was viewing, so it offered a button
 * per person and said plainly that it was a log of who pressed what. Here
 * the session names you, so you can only acknowledge for yourself — every
 * other chip is a readout. */
export function AckBar() {
  const { user, isAdmin } = useAuth();
  const toast = useToast();
  const [people, setPeople] = useState(null);
  const [entries, setEntries] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [{ users }, today] = await Promise.all([peopleApi.list(), acksApi.today()]);
        if (!live) return;
        setPeople(users.filter((u) => u.daily && u.active));
        setEntries(today.entries || {});
      } catch {
        if (live) setPeople([]);
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  async function acknowledge() {
    setBusy(true);
    try {
      const today = await acksApi.acknowledge();
      setEntries(today.entries || {});
      toast("Acknowledged");
    } catch (e) {
      if (e.status !== 401) toast(e.message, true);
    } finally {
      setBusy(false);
    }
  }

  if (people === null) return null;

  if (!people.length) {
    return (
      <div className="ackbar empty">
        <span className="label">Daily review</span>
        <span className="msg">
          Nobody is marked as a daily reviewer yet.{" "}
          {isAdmin ? (
            <>
              Open <Link to="/access">Access</Link> and tick the people who work this
              board each day.
            </>
          ) : (
            <>Ask an administrator to mark who works this board each day.</>
          )}
        </span>
      </div>
    );
  }

  const todayYmd = ymd(new Date());

  /* Saturday and Sunday are not working days. The accountability grid
     leaves them out of its columns, so a weekend can never be a miss — a
     bar that still asked for an acknowledgement would be demanding
     something no record will ever count. It stands down instead. */
  if (isWeekend(todayYmd)) {
    return (
      <div className="ackbar off">
        <span className="label">Daily review &middot; {todayYmd}</span>
        <span className="msg">
          {dayName(todayYmd)} is not a working day — nothing to acknowledge, and
          nothing counted against anyone. The check-back picks up again on Monday.
        </span>
      </div>
    );
  }

  const outstanding = people.filter((p) => !entries[p.email]).length;
  const mine = entries[user.email];
  const iAmDaily = people.some((p) => p.email === user.email);

  return (
    <div className={"ackbar" + (outstanding ? "" : " all")}>
      <span className="label">Daily review &middot; {todayYmd}</span>
      <div className="ackchips">
        {people.map((p) => {
          const at = entries[p.email];
          const label = p.name || p.email;
          if (at) {
            return (
              <span key={p.email} className="ackchip done">
                <b>{label}</b> {clockTime(at.at)}
              </span>
            );
          }
          /* Only your own chip is pressable. Someone else's is a readout of
             what has not happened yet, not a button to do it for them. */
          if (p.email === user.email) {
            return (
              <button
                key={p.email}
                className="ackchip todo"
                type="button"
                onClick={acknowledge}
                disabled={busy}
              >
                <b>{label}</b> acknowledge
              </button>
            );
          }
          return (
            <span key={p.email} className="ackchip waiting">
              <b>{label}</b> pending
            </span>
          );
        })}
      </div>
      <span className="msg">
        {outstanding
          ? outstanding + " still to acknowledge before end of day"
          : "All acknowledged"}
        {!iAmDaily && !mine ? " · you are not a daily reviewer" : ""}
      </span>
    </div>
  );
}
